import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import ms from 'ms';
import { memo, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { WhyCantRetractBidPopover } from '@/components/Popovers/WhyCantRetractBidPopover';
import { RegisterLink } from '@/components/RegisterLink/RegisterLink';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId, getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import {
    getItemSummaryBidderMaxBid,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryCurrency,
    getItemSummaryIsCatalogOnly,
    getItemSummaryLeadingBid,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryStartPrice,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getUiLiveBid, submitLiveBid } from '@/redux/modules/liveBid';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import {
    CompleteAccountModalContext,
    ModalContext,
    openPlaceAbsenteeBidModal,
    openRetractBidModal,
    openSendSellerMessageModal,
} from '@/redux/modules/modal';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';

import { CardCtaBanner } from './CardCtaBanner';

type Props = {
    allowBidding?: boolean;
    itemId: number;
    showBanner?: boolean;
    showCta?: boolean;
};

const CardCtaComponent = (props: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { allowBidding, itemId, showBanner, showCta } = props;
    const bidderId = useAppSelector(getBidderId);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const leadingBidderId = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const location = useLocation();

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);

    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const catalogTitle = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const leadingBidder = leadingBidderId === bidderId;

    const [, setTime] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => setTime(Date.now()), 5000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, registered, suspended } = useAppSelector(
        (state) => getCatalogRegistration(state, catalogId)
    );

    const { submitted } = useAppSelector(getUiLiveBid);
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;

    const showSendSellerMessageModal = useCallback(() => {
        const messageDetails = {};
        const subject = `${formatMessage(['registration_declined'])}: ${catalogTitle}`;
        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogTitle, dispatch, formatMessage, sellerId, sellerName]);

    const checkReadyToBid = useCallback(() => {
        const timeSinceLastMessage = Date.now() - liveUpdateTimestamp;

        return (
            itemId === currentItem &&
            itemId > 0 &&
            networkStatus === 'connected' &&
            status === 'live' &&
            !closed &&
            !won &&
            Boolean(currentAskPrice) &&
            catalogId > 0 &&
            timeSinceLastMessage < ms('5m')
        );
    }, [catalogId, currentAskPrice, itemId, closed, currentItem, networkStatus, status, won, liveUpdateTimestamp]);

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid();

    const showLiveButton = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const linkAndButton = useCallback(() => {
        const increaseText = (
            <span className="mb-sm block [&_a]:inline [&_a]:underline [&_div]:inline [&_span]:inline">
                {formatMessage(['increase_max_of'], {
                    values: {
                        bidAmount: (
                            <FormattedCurrency
                                key="increase-max-of-bid-amount"
                                noFraction
                                sourceCurrency={currency}
                                value={bidderMaxBid}
                            />
                        ),
                    },
                })}
                <Link
                    onClick={() => {
                        dispatch(
                            openPlaceAbsenteeBidModal({
                                bidAmount: 0,
                                isInstantAutopay,
                                itemId,
                                modalContext,
                            })
                        );
                    }}
                >
                    {formatMessage(['increase'])}
                </Link>
            </span>
        );

        let link: ReactNode | '';
        if (suspended || !accountInGoodStanding) {
            link = (
                <Link
                    className="max-[469px]:px-md max-[469px]:py-sm w-full"
                    href="https://help.liveauctioneers.com/article/146-how-do-i-restore-my-suspended-account"
                >
                    {formatMessage(['suspendedBidderBanner', 'button'])}
                </Link>
            );
        } else if (blockedByAuctioneer || declined) {
            link = (
                <ShouldLogInContainer
                    requireCompleteProfile
                    requireLogin
                    runFunction={showSendSellerMessageModal}
                >
                    <Link>{formatMessage(['contact_auctioneer'])}</Link>
                </ShouldLogInContainer>
            );
        } else if (catalogStatus === AuctionStatus.Done) {
            link = <RouteLink to={itemUrl}>{formatMessage(['view_similar_items'])}</RouteLink>;
        } else if (showLiveButton) {
            link = (
                <>
                    <BidConsoleOpener catalogId={catalogId}>
                        <Link color="critical">{formatMessage(['enter_live_auction'])}</Link>
                    </BidConsoleOpener>
                    {!isLotOpen && increaseText}
                </>
            );
        } else {
            link = increaseText;
        }

        let retractButton: ReactNode | '';
        if (catalogStatus === 'online') {
            retractButton = (
                <Caption
                    className="max-[600px]:w-full max-[600px]:flex-[1_0_29px] max-[600px]:text-center mr-auto max-w-[40px] flex-[1_0_25%] cursor-pointer text-text-secondary no-underline [&:focus]:outline-hidden [&>a]:no-underline [&>a]:hover:no-underline [&>a]:focus:outline-hidden [&[disabled]]:invisible"
                    onClick={() => {
                        dispatch(openRetractBidModal(itemId));
                    }}
                >
                    {formatMessage(['retract'])}
                </Caption>
            );
        } else if (catalogStatus === 'live') {
            retractButton = (
                <Caption className="max-[600px]:w-full max-[600px]:flex-[1_0_29px] max-[600px]:text-center mr-auto max-w-[40px] flex-[1_0_25%] cursor-pointer text-text-secondary no-underline [&:focus]:outline-hidden [&>a]:no-underline [&>a]:hover:no-underline [&>a]:focus:outline-hidden [&[disabled]]:invisible">
                    <WhyCantRetractBidPopover />
                </Caption>
            );
        } else {
            retractButton = (
                <Caption className="max-[600px]:w-full max-[600px]:flex-[1_0_29px] max-[600px]:text-center mr-auto max-w-[40px] flex-[1_0_25%] cursor-pointer text-text-secondary no-underline [&:focus]:outline-hidden [&>a]:no-underline [&>a]:hover:no-underline [&>a]:focus:outline-hidden [&[disabled]]:invisible" />
            );
        }
        return (
            <>
                {link}
                {retractButton}
            </>
        );
    }, [
        currency,
        bidderMaxBid,
        suspended,
        accountInGoodStanding,
        blockedByAuctioneer,
        declined,
        catalogStatus,
        showLiveButton,
        dispatch,
        formatMessage,
        isInstantAutopay,
        itemId,
        modalContext,
        showSendSellerMessageModal,
        itemUrl,
        catalogId,
        isLotOpen,
    ]);

    const placeBid = useCallback(() => {
        // dont only rely on the "disabled" button to prevent bidders who havent been approved
        if (accountInGoodStanding && (approved || limited) && checkReadyToBid()) {
            dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId, location));
        } else {
            return false;
        }
    }, [
        accountInGoodStanding,
        approved,
        catalogId,
        checkReadyToBid,
        currency,
        currentAskPrice,
        dispatch,
        itemId,
        limited,
        location,
    ]);

    return isLotOpen && allowBidding ? (
        <div className="[&_button]:max-[500px]:hidden [&_button]:w-full [&>a]:mr-xs [&>a]:flex [&>a]:flex-[1_1_75%] [&>a]:max-[600px]:w-full [&>a]:max-[600px]:flex-[1_0_auto]">
            {!isStoppedFromBidding && registered && !leadingBidder && (
                <Button
                    disabled={submitted}
                    loading={submitted}
                    onClick={placeBid}
                    size="sm"
                    style="critical"
                    variant="filled"
                >
                    {formatMessage(['bidding_console', 'console', 'button', 'bid'], {
                        values: {
                            amount: (
                                <FormattedCurrency
                                    className="ml-2xs"
                                    key="bid-button-amount"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={currentAskPrice}
                                />
                            ),
                        },
                    })}
                </Button>
            )}
            {leadingBidder && (
                <Button
                    className="!cursor-default !border-border-success !bg-background-success-dark"
                    size="sm"
                    variant="filled"
                >
                    {formatMessage(['winning'])}
                </Button>
            )}
            {!registered && (
                <RegisterLink
                    className="block text-center"
                    catalogId={catalogId}
                />
            )}
        </div>
    ) : showCta ? (
        <div className="[&_button]:max-[500px]:hidden [&_button]:w-full [&>a]:mr-xs [&>a]:flex [&>a]:flex-[1_1_75%] [&>a]:max-[600px]:w-full [&>a]:max-[600px]:flex-[1_0_auto]">
            {linkAndButton()}
        </div>
    ) : showBanner && catalogStatus !== AuctionStatus.Done ? (
        <div className="[&_button]:max-[500px]:hidden [&_button]:w-full [&>a]:mr-xs [&>a]:flex [&>a]:flex-[1_1_75%] [&>a]:max-[600px]:w-full [&>a]:max-[600px]:flex-[1_0_auto]">
            <CardCtaBanner
                catalogId={catalogId}
                itemId={itemId}
            />
        </div>
    ) : null;
};

export const CardCta = memo<Props>(CardCtaComponent);
